import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { IoArrowBackOutline, IoSaveOutline, IoWarningOutline, IoCopyOutline } from 'react-icons/io5';
import Navbar from '../lab/Navbar';
import labService from '../lab/labService';
import '../wallet/css/ManageLicences.css';

const CreateVersionAdmin = () => {
  const { modelId } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const bumpVersion = (versionStr) => {
    if (!versionStr) return 'v2.0.0';
    const match = versionStr.match(/^(v?)(\d+)(.*)/i);
    if (match) {
      const prefix = match[1];
      const major = parseInt(match[2], 10);
      const rest = match[3];
      return `${prefix}${major + 1}${rest}`;
    }
    return `${versionStr}-new`;
  };

  useEffect(() => {
    const fetchModelToCopy = async () => {
      const token = JSON.parse(localStorage.getItem('user'))?.access;
      if (!token) return;

      try {
        const response = await labService.getModelDetailsAdmin(modelId, token);
        const oldData = response.data;

        setFormData({
          ...oldData,
          version_name: bumpVersion(oldData.version_name),
          model_exec_path: oldData.model_exec_path || '',
          scaler_exec_path: oldData.scaler_exec_path || '',
          data_path: oldData.data_path || ''
        });
      } catch (error) {
        console.error("Error loading model:", error);
        alert("Failed to load base model data.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchModelToCopy();
  }, [modelId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    const token = JSON.parse(localStorage.getItem('user'))?.access;

    try {
      const payload = {
        model_name: formData.model_name,
        endpoint_type: formData.endpoint_type?.id || formData.endpoint_type, // Obejście dla serializers
        version_name: formData.version_name,
        algorithm_type: formData.algorithm_type,
        response_class: formData.response_class,
        model_category: formData.model_category,
        method_type: formData.method_type,
        model_price: parseFloat(formData.model_price),
        model_exec_path: formData.model_exec_path,
        scaler_exec_path: formData.scaler_exec_path,
        data_path: formData.data_path,
        descriptors: formData.descriptors,
        model_details: formData.model_details
      };

      await labService.createModelAdmin(payload, token);

      navigate('/manage-models');
    } catch (error) {
      console.error("Error creating new version:", error);
      alert("Failed to create new version. Check paths and formats.");
    } finally {
      setIsSaving(false);
    }
  };

  const labelStyle = { display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#4a5568', fontSize: '0.9rem' };
  const inputStyle = { width: '100%', paddingLeft: '15px', fontSize: '14px' };

  if (isLoading || !formData) return <div className='lab-container'><Navbar /><div className="loading-container"><div className="spinner"></div></div></div>;

  return (
    <div className='lab-container'>
      <Navbar />
      <div className="manage-licenses-container" style={{ padding: '40px' }}>

        <header style={{ display: 'flex', alignItems: 'center', marginBottom: '30px', gap: '20px' }}>
          <IoArrowBackOutline
            className="icon-action-danger"
            style={{ fontSize: '30px', color: '#1B4480' }}
            onClick={() => navigate(-1)}
          />
          <div>
            <div style={{ color: '#718096', fontSize: '0.9rem', textTransform: 'uppercase', fontWeight: 'bold' }}>
              Base Model: {formData.model_name}
            </div>
            <h2 className='h2-title' style={{ margin: 0, color: '#1B4480', display: 'flex', alignItems: 'center', gap: '10px' }}>
              Create New Version
            </h2>
          </div>
        </header>

        <div style={{ backgroundColor: '#fffaf0', borderLeft: '5px solid #dd6b20', padding: '20px 25px', borderRadius: '6px', marginBottom: '35px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
          <h3 style={{ color: '#dd6b20', margin: '0 0 10px 0', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1.2rem' }}>
            <IoWarningOutline size="24px" /> Attention: Technical Paths Update Required
          </h3>
          <p style={{ margin: 0, color: '#744210', fontSize: '0.95rem', lineHeight: '1.6' }}>
            You are deploying a new version of <strong>{formData.model_name}</strong>.
            You must provide the exact paths to the updated <strong>scaler</strong>, <strong>model executable (.pickle)</strong>, and <strong>data source</strong>.
            <br />⚠️ <em>Incorrect or missing paths will result in prediction failures. All other configurations (descriptors, statistics) will be cloned automatically.</em>
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ backgroundColor: '#fff', padding: '35px', borderRadius: '8px', border: '1px solid #e2e8f0', maxWidth: '800px' }}>

          <div style={{ display: 'flex', gap: '20px', marginBottom: '30px' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label style={labelStyle}>New Version Target</label>
              <input
                className="search-input"
                style={{ ...inputStyle, backgroundColor: '#ebf4ff', color: '#2b6cb0', fontWeight: 'bold', border: '1px solid #bee3f8' }}
                value={formData.version_name}
                onChange={e => setFormData({ ...formData, version_name: e.target.value })}
                required
              />
            </div>
          </div>

          <h3 style={{ color: '#1B4480', marginTop: 0, marginBottom: '20px' }}>Update Technical Paths</h3>

          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Model Executable Path (.pickle)</label>
            <input
              className="search-input"
              style={inputStyle}
              value={formData.model_exec_path}
              onChange={e => setFormData({ ...formData, model_exec_path: e.target.value })}
              required
            />
          </div>

          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Scaler Path</label>
            <input
              className="search-input"
              style={inputStyle}
              value={formData.scaler_exec_path}
              onChange={e => setFormData({ ...formData, scaler_exec_path: e.target.value })}
              required
            />
          </div>

          <div className="form-group" style={{ marginBottom: '30px' }}>
            <label style={labelStyle}>Data Source Path (.xlsx)</label>
            <input
              className="search-input"
              style={inputStyle}
              value={formData.data_path}
              onChange={e => setFormData({ ...formData, data_path: e.target.value })}
              required
            />
          </div>

          <div style={{ backgroundColor: '#f7fafc', padding: '15px 20px', borderRadius: '6px', marginBottom: '30px', display: 'flex', alignItems: 'center', gap: '10px', color: '#718096', fontSize: '0.9rem' }}>
            <IoCopyOutline size="20px" />
            <span>All underlying descriptors and settings from <strong>{formData.version_name.replace(/(\d+)/, (match) => parseInt(match) - 1)}</strong> will be automatically cloned to this new version.</span>
          </div>

          <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-start' }}>
            <button type="submit" className="btn btn-primary" style={{ padding: '12px 35px' }} disabled={isSaving}>
              <IoSaveOutline size="20px" /> {isSaving ? 'Deploying Version...' : 'Deploy New Version'}
            </button>
            <button type="button" className="btn btn-neutral" onClick={() => navigate(-1)}>
              Cancel
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default CreateVersionAdmin;
